import React from "react";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Paper,
  Chip,
  Divider,
} from "@mui/material";
import Carousel from "react-material-ui-carousel";
import DOMPurify from 'dompurify';
import { useParams } from "react-router-dom";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useGetGalleryByIdQuery } from "../api/galleriesApi";

const GalleryDetailPage = () => {
  const { id } = useParams();
  const { data, isLoading, isError } = useGetGalleryByIdQuery(id!);

  if (isLoading) {
    return (
      <Box height="70vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data?.data) {
    return (
      <Box height="70vh" display="flex" alignItems="center" justifyContent="center">
        <Typography color="error">Failed to load gallery details.</Typography>
      </Box>
    );
  }

  const { title, description, images, category, is_featured, created_at } = data.data;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {title}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonthIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              {new Date(created_at).toLocaleDateString()}
            </Typography>
          </Box>
          
          {category && (
            <Chip 
              label={category.name} 
              color="primary" 
              size="small" 
            />
          )}
          
          {is_featured && (
            <Chip 
              label="Featured" 
              color="success" 
              size="small" 
            />
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {images && images.length > 0 ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Images ({images.length})
            </Typography>
            <Carousel
              autoPlay={false}
              animation="slide"
              navButtonsAlwaysVisible
              indicators
            >
              {images.map((image: string, index: number) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <img
                    src={image}
                    alt={`${title} - Image ${index + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '500px',
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }}
                  />
                </Box>
              ))}
            </Carousel>
          </Box>
        ) : (
          <Box sx={{ mb: 3, textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No images available
            </Typography>
          </Box>
        )}

        {description && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {description}
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default GalleryDetailPage;